import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { reportError } from '@/lib/errorReporting';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import type { Inquilino } from '@/services/tenantService';
import { toast } from 'sonner';
import { buildingService, type Edificio } from '@/services/buildingService';
import { unitService, type Unidad } from '@/services/unitService';

interface TenantFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (tenant: Inquilino) => void;
  initialData?: Inquilino | null;
  unitId?: string; // Optional, for creating a tenant directly associated with a unit
}

const TenantForm: React.FC<TenantFormProps> = ({ isOpen, onClose, onSave, initialData, unitId }) => {
  const [buildings, setBuildings] = useState<Edificio[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<string | undefined>(undefined);
  const [units, setUnits] = useState<Unidad[]>([]);

  const formSchema = z.object({
    nombre_completo: z.string().min(1, { message: "El nombre completo es requerido." }),
    email: z.string().email({ message: "Debe ser un email válido." }).optional().or(z.literal("")),
    telefono: z.string().optional(),
    fecha_inicio_contrato: z.string().optional(),
    fecha_fin_contrato: z.string().optional(),
    activo: z.boolean().default(true).optional(),
    building_selection: z.string().optional(),
    id_unidad: z.string().optional(), // Default to optional
  }).superRefine((data, ctx) => {
    // Make id_unidad required only when creating a new tenant from TenantsPage
    if (!initialData && !unitId && !data.id_unidad) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La unidad es requerida.",
        path: ['id_unidad'],
      });
    }
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nombre_completo: "",
      email: "",
      telefono: "",
      fecha_inicio_contrato: "",
      fecha_fin_contrato: "",
      activo: true,
      id_unidad: unitId || undefined, // Set initial unitId if provided
      building_selection: undefined,
    },
  });

  useEffect(() => {
    if (initialData) {
      form.reset({
        nombre_completo: initialData.nombre_completo || "",
        email: initialData.email || "",
        telefono: initialData.telefono || "",
        fecha_inicio_contrato: initialData.fecha_inicio_contrato || "",
        fecha_fin_contrato: initialData.fecha_fin_contrato || "",
        activo: initialData.activo !== undefined ? initialData.activo : true,
        id_unidad: initialData.id_unidad || undefined,
      });
      if (initialData.id_unidad) {
        // Fetch the building for the initial unit to pre-select the building dropdown
        unitService.getUnitsByBuilding(initialData.id_unidad).then(data => {
          if (data && data.length > 0) {
            buildingService.getBuildings().then(blds => {
              const buildingOfUnit = blds.find(bld => bld.id_edificio === data[0].id_edificio);
              if (buildingOfUnit) {
                setSelectedBuildingId(buildingOfUnit.id_edificio);
              }
            });
          }
        });
      }
    } else {
      form.reset();
      setSelectedBuildingId(undefined);
    }
  }, [initialData, form, unitId]);

  // Fetch buildings on component mount
  useEffect(() => {
    const fetchBuildings = async () => {
      try {
        const data = await buildingService.getBuildings();
        setBuildings(data);
      } catch (err: unknown) {
        reportError(err, {
          component: 'TenantForm',
          action: 'fetchBuildings',
        });
        toast.error("Error al cargar los edificios.");
      }
    };
    fetchBuildings();
  }, []);

  // Fetch units when selectedBuildingId changes
  useEffect(() => {
    if (selectedBuildingId) {
      const fetchUnits = async () => {
        try {
          const data = await unitService.getUnitsByBuilding(selectedBuildingId);
          setUnits(data);
        } catch (err: unknown) {
          reportError(err, {
            component: 'TenantForm',
            action: 'fetchUnits',
            extra: { buildingId: selectedBuildingId }
          });
          toast.error("Error al cargar las unidades.");
        }
      };
      fetchUnits();
    } else {
      setUnits([]);
    }
  }, [selectedBuildingId]);

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    onSave({
      ...initialData,
      id_unidad: values.id_unidad || unitId, // Use selected unitId or prop unitId
      nombre_completo: values.nombre_completo,
      email: values.email || undefined,
      telefono: values.telefono || undefined,
      fecha_inicio_contrato: values.fecha_inicio_contrato || undefined,
      fecha_fin_contrato: values.fecha_fin_contrato || undefined,
      activo: values.activo,
    });
    onClose();
    toast.success(initialData ? "Inquilino actualizado exitosamente." : "Inquilino creado exitosamente.");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initialData ? "Editar Inquilino" : "Agregar Nuevo Inquilino"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="nombre_completo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre Completo</FormLabel>
                  <FormControl>
                    <Input placeholder="Nombre del Inquilino" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="email@ejemplo.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="telefono"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Teléfono</FormLabel>
                  <FormControl>
                    <Input placeholder="Ej. +58 412 1234567" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="fecha_inicio_contrato"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fecha Inicio Contrato</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="fecha_fin_contrato"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fecha Fin Contrato</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="activo"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow-sm">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>¿Inquilino Activo?</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            {/* Building and Unit selection only for new tenants from TenantsPage */}
            {!initialData && !unitId && (
              <>
                <FormField
                  control={form.control}
                  name="building_selection" // Dummy field for building selection
                  render={(/* { field } */) => (
                    <FormItem>
                      <FormLabel>Edificio</FormLabel>
                      <Select onValueChange={(value) => {
                        setSelectedBuildingId(value);
                        form.setValue('id_unidad', undefined); // Reset unit selection
                      }} value={selectedBuildingId}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona un edificio" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {buildings.map((building) => (
                            <SelectItem key={building.id_edificio} value={building.id_edificio!}>
                              {building.nombre_edificio}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="id_unidad"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unidad</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona una unidad" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {units.map((unit) => (
                            <SelectItem key={unit.id_unidad} value={unit.id_unidad!}>
                              {unit.numero_unidad}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <Button type="submit" className="w-full">Guardar</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default TenantForm;
